import { useNavigate } from 'react-router-dom'
import Add from '@mui/icons-material/Add'
import { Divider, Grid } from '@mui/material'
import {
  GENERIC_FOREIGN_CUSTOMERS_RFC,
  GENERIC_NATIONAL_CUSTOMERS_RFC,
  ROUTES,
} from '../../../../constants'
import { CatalogueEntry } from 'models/catalogues'
import Client from 'models/client'
import { Concept, TInvoiceDraftMenuOptions } from 'models/invoices.models'
import RazonSocial from 'models/razonSocial'
import { INVOICE_DRAFT_MENU_OPTIONS } from 'lib/enums/invoices.enums'
import Button from 'components/Shared/Button/Button'
import SydButtonList from 'components/Shared/SydButtonList/SydButtonList'
import Dropdown from 'components/FormComponents/Dropdown/Dropdown'
import Form from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import { DisplayTotalDetails } from 'components/Invoices/DisplayTotal/DisplayTotal'
import ScrollSpy from 'components/Invoices/ScrollSpy/ScrollSpy'
import AddConceptForm from '../AddIncomeCFDIConceptContainer/AddIncomeCFDIConceptContainer'
import useAddIncomeCFDIContainer from './useAddIncomeCFDIContainer'
import './styles.scss'

export interface IAddIncomeCFDIContainerProps {
  updatePDFDocument: (pdf?: string) => void
  togglePDFViewer: () => void
  downloadPDFHandler: (pdf: string | null) => void
  cleanUpAndLeave: (backToInvoices?: boolean) => void
}

const AddIncomeCFDIContainer = ({
  updatePDFDocument,
  togglePDFViewer,
  downloadPDFHandler,
  cleanUpAndLeave,
}: IAddIncomeCFDIContainerProps) => {
  const navigate = useNavigate()

  const {
    clients,
    emisores,
    form,
    formasPago,
    lastFolio,
    loading,
    metodosPago,
    monedas,
    months,
    periods,
    products,
    serieCatalog,
    taxes,
    usosCfdi,
    valid,
    years,
    saveInvoiceCreationProgressMutation,
    draftMenuOptionActions,
    invoiceDraftMenuOptions,
    updateState,
    saveValueToStore,
    filterClients,
    setEmisor,
    setSerie,
    handleNewSerie,
    handleCleanSerie,
    addConcept,
    onAdd,
  } = useAddIncomeCFDIContainer({
    updatePDFDocument,
    togglePDFViewer,
    downloadPDFHandler,
    cleanUpAndLeave,
  })

  return (
    <div className="container-add-income-cfdi">
      <article className="scrollspy-container">
        <ScrollSpy
          items={[
            { id: 'section-1', label: '1', tooltip: 'Información del emisor' },
            {
              id: 'section-2',
              label: '2',
              tooltip: 'Información del receptor',
            },
            { id: 'section-3', label: '3', tooltip: 'CFDI' },
            { id: 'section-4', label: '4', tooltip: 'Conceptos' },
            { id: 'section-5', label: '5', tooltip: 'Serie' },
          ]}
        />
      </article>
      <article className="form-container">
        <Form
          id="create-invoice-form"
          onSubmit={onAdd}
          onValidity={(v: boolean) =>
            updateState(draft => {
              draft.valid = v
            })
          }>
          <div id="section-1" className="content">
            <h3>Información del emisor</h3>
            <Divider className="separator" />
            <p className="form-label-alternative">Selecciona el emisor</p>
            <Grid container>
              <Grid item xs={5}>
                <AutocompleteField
                  name="emisor"
                  placeholder="Busca por alias, nombre, razón social o RFC"
                  catalogue={emisores}
                  value={form?.emisor}
                  onValue={(v: RazonSocial) => setEmisor(v)}
                  getOptionLabel={(o: any) => o.alias || o.name}
                  getOptionDisabled={(o: RazonSocial) =>
                    !Boolean(o.passwordCsd)
                  }
                  renderOption={(o: any) => (
                    <div
                      className={`client-option ${!o.passwordCsd ? 'disabled' : ''}`.trim()}>
                      <div className="alias">{o.name}</div>
                      <div className="rfc">{o.rfc.toUpperCase()}</div>
                      {!o.passwordCsd && (
                        <>
                          <div className="break" />
                          <div className="advice">Este emisor no tiene CSD</div>
                        </>
                      )}
                    </div>
                  )}
                  getOptionSelected={(
                    option: RazonSocial,
                    value: RazonSocial
                  ) => option.idRfc === value.idRfc}
                  fullWidth
                />
              </Grid>
              {!!form.emisor && (
                <Grid item xs={12}>
                  <div className="emisor-info">
                    <Grid container spacing={4}>
                      <Grid item xs={3}>
                        <TextField
                          name="emisor_rfc"
                          label="RFC"
                          value={form.emisor?.rfc}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{
                            readOnly: true,
                            style: { textTransform: 'uppercase' },
                          }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="emisor_name"
                          label="Nombre o razón social"
                          value={form.emisor?.name}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="emisor_taxResidence"
                          label="Régimen fiscal"
                          value={form?.emisor?.taxResidence?.label}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          fullWidth
                          exclude
                        />
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
          <div id="section-2" className="content">
            <h3>Información del receptor</h3>
            <Divider className="separator" />
            <p className="form-label-alternative">
              Seleccionalo desde tu catálogo de clientes
            </p>
            <Grid container>
              <Grid item xs={5}>
                <AutocompleteField
                  name="receptor"
                  placeholder="Busca por alias, nombre, razón social o RFC"
                  catalogue={clients}
                  value={form?.receptor}
                  onValue={(v: Client) => saveValueToStore('receptor', v)}
                  onNewValue={() => navigate(ROUTES.ADD_CLIENT)}
                  getOptionLabel={(o: any) => o.alias || o.name}
                  customFilter={filterClients}
                  renderOption={(o: any) =>
                    o.flag === 'newValue' ? (
                      <div className="new-option">
                        <Add />
                        <div className="new-text">
                          {'AGREGAR NUEVO CLIENTE'}
                        </div>
                      </div>
                    ) : (
                      <div className="clientit -option">
                        <div className="alias">{o.alias}</div>
                        <div className="rfc">{o.rfc.toUpperCase()}</div>
                      </div>
                    )
                  }
                  getOptionDisabled={(o: any) => {
                    if (o.flag && o.flag === 'newValue') return false
                    return !o.taxRegime
                  }}
                  getOptionSelected={(option: Client, value: Client) =>
                    option.id === value.id
                  }
                  fullWidth
                />
              </Grid>
              {!!form.receptor && (
                <Grid item xs={12}>
                  <div className="receptor-info">
                    <Grid container spacing={4}>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_rfc"
                          label="RFC"
                          value={form.receptor?.rfc}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{
                            readOnly: true,
                            style: { textTransform: 'uppercase' },
                          }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_name"
                          label="Nombre o razón social"
                          value={form.receptor?.name}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_email"
                          label="Correo electrónico"
                          value={form.receptor?.email}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_phone"
                          label="Teléfono"
                          value={form.receptor?.phone}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <p className="form-header">Dirección</p>
                    <Grid container spacing={4}>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_zipCode"
                          label="Código postal"
                          value={form.receptor?.zipCode || '---'}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_branch"
                          label="Nombre sucursal"
                          value={form.receptor?.branch || '---'}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="receptor_contant"
                          label="Contacto"
                          value={form.receptor?.contact || '---'}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{ readOnly: true }}
                          exclude
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Dropdown
                          title={'Dirección completa (opcional)'}
                          bordered
                          disabled>
                          <></>
                        </Dropdown>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
          <div id="section-3" className="content">
            <h3>CFDI</h3>
            <Divider className="separator" />
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <AutocompleteField
                  name="moneda"
                  label="Moneda"
                  catalogue={monedas}
                  value={form?.moneda}
                  onChange={(v: CatalogueEntry) => {
                    saveValueToStore('moneda', v)
                  }}
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => (
                    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
                  )}
                  getOptionSelected={(
                    option: CatalogueEntry,
                    value: CatalogueEntry
                  ) => option.idCatalogValues === value.idCatalogValues}
                  fullWidth
                  required
                />
              </Grid>
              {!!form.moneda &&
                !['MXN', 'XXX'].includes(form.moneda?.value) && (
                  <Grid item xs={4}>
                    <NumericField
                      name="tipoCambio"
                      label="Tipo de cambio"
                      value={form?.tipoCambio}
                      onValue={(v: string) => saveValueToStore('tipoCambio', v)}
                      onChange={(v: string) =>
                        v === '' && saveValueToStore('tipoCambio', '')
                      }
                      format="exchangeRate"
                      fullWidth
                      required
                    />
                  </Grid>
                )}
              <Grid item xs={4}>
                <AutocompleteField
                  name="metodoPago"
                  label="Método de pago"
                  catalogue={metodosPago}
                  value={form?.metodoPago}
                  onChange={(v: CatalogueEntry) =>
                    saveValueToStore('metodoPago', v)
                  }
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => (
                    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
                  )}
                  getOptionSelected={(
                    option: CatalogueEntry,
                    value: CatalogueEntry
                  ) => option.idCatalogValues === value.idCatalogValues}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteField
                  name="formaPago"
                  label="Forma de pago"
                  catalogue={formasPago}
                  value={form?.formaPago}
                  onChange={(v: CatalogueEntry) =>
                    saveValueToStore('formaPago', v)
                  }
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => (
                    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
                  )}
                  getOptionSelected={(
                    option: CatalogueEntry,
                    value: CatalogueEntry
                  ) => option.idCatalogValues === value.idCatalogValues}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteField
                  name="usoCfdi"
                  label="Uso de Comprobante"
                  catalogue={usosCfdi}
                  value={form?.usoCfdi}
                  onChange={(v: CatalogueEntry) =>
                    saveValueToStore('usoCfdi', v)
                  }
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => (
                    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
                  )}
                  getOptionSelected={(
                    option: CatalogueEntry,
                    value: CatalogueEntry
                  ) => option.idCatalogValues === value.idCatalogValues}
                  fullWidth
                  required
                />
              </Grid>
              {[
                GENERIC_NATIONAL_CUSTOMERS_RFC,
                GENERIC_FOREIGN_CUSTOMERS_RFC,
              ].includes(form?.receptor?.rfc || '') && (
                <>
                  <Grid item xs={4}>
                    <AutocompleteField
                      name="period"
                      label={'Periodicidad'}
                      catalogue={periods}
                      value={form?.period}
                      onChange={(v: CatalogueEntry) =>
                        saveValueToStore('period', v)
                      }
                      getOptionSelected={(
                        option: CatalogueEntry,
                        value: CatalogueEntry
                      ) => option.idCatalogValues === value.idCatalogValues}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <AutocompleteField
                      name="year"
                      label={'Año'}
                      catalogue={years}
                      value={form?.year}
                      onChange={(v: CatalogueEntry) =>
                        saveValueToStore('year', v)
                      }
                      getOptionSelected={(
                        option: CatalogueEntry,
                        value: CatalogueEntry
                      ) => option.idCatalogValues === value.idCatalogValues}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <AutocompleteField
                      name="month"
                      label={'Mes'}
                      catalogue={months.slice(0, 12)}
                      value={form?.month}
                      onChange={(v: CatalogueEntry) =>
                        saveValueToStore('month', v)
                      }
                      getOptionSelected={(
                        option: CatalogueEntry,
                        value: CatalogueEntry
                      ) => option.idCatalogValues === value.idCatalogValues}
                      fullWidth
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} />
              <Grid item xs={8}>
                <p className="form-label">
                  Condiciones de pago - Información adicional
                </p>
                <TextField
                  variant="outlined"
                  fieldType="alphanumericWithSpecialCharacters"
                  name="condiciones"
                  placeholder="Información adicional"
                  value={form?.condiciones}
                  onValue={(v: string) => saveValueToStore('condiciones', v)}
                  onChange={(v: string) =>
                    v === '' && saveValueToStore('condiciones', '')
                  }
                  inputProps={{ maxLength: 1000 }}
                  multiline
                  minRows={4}
                  maxRows={4}
                  withCounter
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
          <div id="section-4" className="content">
            <h3>Conceptos</h3>
            <Divider className="separator" />
            <p className="form-label-alternative">
              Selecciona un concepto guardado anteriormente o agrega uno nuevo
            </p>
            <Grid
              className="section-add-item"
              container
              justifyContent="space-between">
              <Grid item xs={5}>
                <AutocompleteField
                  name="conceptos"
                  placeholder="Buscar"
                  catalogue={products}
                  fullWidth
                  getOptionLabel={(o: any) => `${o.name}`}
                  onSelectValue={addConcept}
                  exclude
                />
              </Grid>
              <Grid item>
                <Button
                  id="newConceptBtn"
                  variant="outlined"
                  color="warning"
                  startIcon={<Add />}
                  onClick={() => addConcept()}>
                  Nuevo Concepto
                </Button>
              </Grid>
            </Grid>
            {form.conceptos.map((c, i) => (
              <AddConceptForm
                id={i}
                // key={c.uuid}
                key={i}
                title={`Concepto ${i + 1}`}
                // uuid={c.uuid}
                uuid={String(i)}
                clave={c.satKey ?? c.claveSat}
                unidad={c.claveUnitSat}
                precio={c.price}
                cantidad={c.cantidad}
                descuento={c.descuento}
                descripcion={c.description}
                impuestos={c.impuestos}
                importacion={c.importacion}
                objImp={c.objImp}
                impuestosCat={taxes}
                predial={c.predial}
              />
            ))}
            <div className="invoice-breakdown">
              <DisplayTotalDetails concepts={form?.conceptos as Concept[]} />
            </div>
          </div>
          <div id="section-5" className="content">
            <h3>Serie</h3>
            <Divider className="separator" />
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <p className="form-label-alternative">Serie</p>
                <AutocompleteField
                  name="serie"
                  placeholder="Busca por serie"
                  catalogue={serieCatalog}
                  value={form.serie ? form.serie : null}
                  onSelectValue={(v: any) => setSerie(v)}
                  onValue={(v: any) => handleCleanSerie(v)}
                  onNewValue={(value: string) => handleNewSerie(value)}
                  getOptionLabel={(o: any) => o.label}
                  renderOption={(o: any) =>
                    o.flag === 'newValue' ? (
                      <div className="new-option">
                        <Add />
                        <div className="new-text">AGREGAR NUEVA SERIE</div>
                      </div>
                    ) : (
                      <div className={`serie-option`.trim()}>
                        <div className="name">{o.value}</div>
                      </div>
                    )
                  }
                  getOptionSelected={(option: any, value: any) =>
                    option.value === value.value
                  }
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <p className="form-label-alternative">Folio</p>
                <TextField
                  aria-label="select-folio"
                  name="folio"
                  value={form?.folio}
                  onChange={(v: string) => saveValueToStore('folio', v)}
                  fullWidth
                  required
                />
                {lastFolio && (
                  <label className="form-label-alternative">{`Ultimo Folio: ${lastFolio}`}</label>
                )}
              </Grid>
            </Grid>
          </div>
          <Divider className="separator" />
          <article className="actions">
            <SydButtonList
              label="Prefactura"
              options={invoiceDraftMenuOptions}
              handleClickedOption={value => {
                draftMenuOptionActions[value as TInvoiceDraftMenuOptions](form)
              }}
              loading={saveInvoiceCreationProgressMutation.isLoading || loading}
              menuDirection="top"
            />
            <div className="right-actions">
              <Button
                variant="outlined"
                color="secondary"
                activity={saveInvoiceCreationProgressMutation.isLoading}
                onClick={() =>
                  draftMenuOptionActions[
                    INVOICE_DRAFT_MENU_OPTIONS.GUARDAR as TInvoiceDraftMenuOptions
                  ](form)
                }>
                Guardar progreso
              </Button>
              <Button
                id="addCfdiBtn"
                variant="contained"
                type="submit"
                form="create-invoice-form"
                color="secondary"
                activity={loading}
                disabled={
                  saveInvoiceCreationProgressMutation.isLoading ||
                  !form.emisor ||
                  !form.receptor ||
                  form.conceptos.length === 0 ||
                  !valid
                }>
                Timbrar
              </Button>
            </div>
          </article>
        </Form>
      </article>
    </div>
  )
}

export default AddIncomeCFDIContainer
